import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HistoryIcon from '@material-ui/icons/History';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import api from '../../../../services/api';
import { Main } from '../../../../components/Main';
import { ROUTES } from '../../../../constants';
import { RouteTimelineDrawer } from '../../components/RouteTimelineDrawer';
import { LiveOperationsMap } from '../../components/LiveOperationsMap';
import { DriverScorecardTab } from '../../components/DriverScorecardTab';
import { LeadTimeTab } from '../../components/LeadTimeTab';
import { WarehouseTab } from '../../components/WarehouseTab';
import { OccurrencesTab } from '../../components/OccurrencesTab';
import { useLocalStorage } from '../../../../hooks/localStorage';
import { OrderCardClick } from '../Dashboard';

type Period = 'today' | '7d' | '30d' | 'month' | 'prev-month';

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Hoje',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  month: 'Mês atual',
  'prev-month': 'Mês anterior',
};

interface ILiveRoute {
  routeId: number;
  routeName: string;
  driverName: string | null;
  totalStops: number;
  delivered: number;
  undelivered: number;
  remaining: number;
  lastSyncAt: string | null;
  minutesSinceSync: number | null;
  projectedEndAt: string | null;
  situation: 'on-pace' | 'no-signal' | 'late' | 'last-stop' | 'not-started';
  position: { lat: number; long: number; heading: number | null; at: string } | null;
}

interface IDashboardData {
  generatedAt: string;
  kpis: {
    totalPeriod: number;
    totalDelta: number | null;
    awaitingSeparation: number;
    awaitingDueToday: number;
    inRoute: { stops: number; routes: number; notStarted: number };
    operationalWindowDays: number;
    deliveredPeriod: number;
    deliveredDelta: number | null;
    late: number;
    partialsOpen: number;
    partialPendingItems: number;
  };
  liveRoutes: ILiveRoute[];
  actionNeeded: {
    late: {
      total: number;
      noSeparation: number;
      partialStalled: number;
      awaitingRoute: number;
    };
    blockingProducts: { description: string; ordersBlocked: number }[];
    occurrencesToday: { reason: string; total: number }[];
  };
  deliveriesByHour: { hour: number; total: number }[];
  funnel: {
    created: number;
    conferred: number;
    routed: number;
    delivered: number;
  };
  quality: {
    firstShipmentRate: number | null;
    avgStopMinutes: number | null;
    undeliveredRate: number | null;
    photoSignatureRate: number | null;
  };
}

const REFRESH_MS = 30_000;

const ink = '#3A3A3A';
const muted = '#757575';
const blue = '#0E52A0';
const blueSoft = '#B7C9DE';
const good = '#2E7D32';
const goodBg = '#E8F5E9';
const warn = '#B26A00';
const warnBg = '#FFF3E0';
const bad = '#C62828';
const badBg = '#FDEBEA';
const track = '#EDF0F4';

const card: React.CSSProperties = {
  border: '1px solid #E7E9ED',
  borderRadius: 12,
  padding: '14px 16px',
  height: '100%',
  boxShadow: 'none',
};

const chip = (color: string, background: string): React.CSSProperties => ({
  background,
  color,
  fontSize: 11,
  fontWeight: 700,
  borderRadius: 999,
  padding: '4px 10px',
  whiteSpace: 'nowrap',
});

const situationChip = (route: ILiveRoute) => {
  switch (route.situation) {
    case 'not-started':
      return <span style={chip('#616161', '#EEEEEE')}>NÃO INICIADA</span>;
    case 'no-signal':
      return (
        <span style={chip(warn, warnBg)}>
          SEM SINAL{' '}
          {route.minutesSinceSync !== null &&
            `· há ${route.minutesSinceSync} min`}
        </span>
      );
    case 'late':
      return (
        <span style={chip(bad, badBg)}>
          ATRASADA{' '}
          {route.projectedEndAt &&
            `· fim ~${formatHour(route.projectedEndAt)}`}
        </span>
      );
    case 'last-stop':
      return <span style={chip(good, goodBg)}>ÚLTIMA PARADA</span>;
    default:
      return (
        <span style={chip(good, goodBg)}>
          NO RITMO{' '}
          {route.projectedEndAt &&
            `· fim ~${formatHour(route.projectedEndAt)}`}
        </span>
      );
  }
};

const formatHour = (iso: string) => {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;
};

const syncLabel = (route: ILiveRoute) => {
  if (route.situation === 'not-started')
    return <span style={{ color: muted }}>—</span>;
  if (route.minutesSinceSync === null)
    return <span style={{ color: muted }}>nunca</span>;
  if (route.minutesSinceSync <= 1)
    return <span style={{ color: good, fontWeight: 600 }}>agora</span>;
  const color = route.minutesSinceSync > 30 ? warn : good;
  return (
    <span style={{ color, fontWeight: 600 }}>
      há {route.minutesSinceSync} min
    </span>
  );
};

const HBar = ({
  label,
  value,
  max,
  color = '#5B87BC',
}: {
  label: string;
  value: number;
  max: number;
  color?: string;
}) => (
  <Box display="flex" alignItems="center" gridGap={10}>
    <span
      title={label}
      style={{
        fontSize: 13,
        width: 130,
        color: ink,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </span>
    <Box flexGrow={1} height={14} borderRadius={4} bgcolor={track} overflow="hidden">
      <Box
        height={14}
        borderRadius={4}
        bgcolor={color}
        width={`${max > 0 ? (value / max) * 100 : 0}%`}
      />
    </Box>
    <span style={{ fontSize: 13, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
      {value}
    </span>
  </Box>
);

const Kpi = ({
  label,
  value,
  detail,
  color = ink,
  detailColor = muted,
  info,
  onClick,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  detail?: React.ReactNode;
  color?: string;
  detailColor?: string;
  info?: string;
  onClick?: () => void;
}) => (
  <Paper
    style={{ ...card, cursor: onClick ? 'pointer' : 'default' }}
    variant="outlined"
    onClick={onClick}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography style={{ fontSize: 13, color: muted }}>{label}</Typography>
      {info && (
        <Tooltip title={info} arrow>
          <InfoOutlinedIcon
            style={{ fontSize: 15, color: '#B0BEC5' }}
            onClick={(event) => event.stopPropagation()}
          />
        </Tooltip>
      )}
    </Box>
    <Typography style={{ fontSize: 30, fontWeight: 700, color }}>
      {value}
    </Typography>
    {detail && (
      <Typography style={{ fontSize: 12, fontWeight: 600, color: detailColor }}>
        {detail}
      </Typography>
    )}
  </Paper>
);

export const ManagementDashboard = () => {
  const history = useHistory();
  const [period, setPeriod] = useState<Period>('today');
  // Onda 1: abas do Painel — 0 Visão Geral · 1 Motoristas · 2 Lead Time · 3 Armazém
  const [tab, setTab] = useState(0);
  const [periodAnchor, setPeriodAnchor] = useState<null | HTMLElement>(null);
  const [timelineRouteId, setTimelineRouteId] = useState<number | null>(null);
  const [mapMode, setMapMode] = useState<'off' | 'live' | 'regional'>('off');
  const [, setOrderCardClick] = useLocalStorage<OrderCardClick>(
    'orderCardClick',
    'order'
  );

  const openOrders = (type: OrderCardClick) => {
    setOrderCardClick(type);
    history.push(ROUTES.order);
  };

  const openRoute = (routeName: string) => {
    localStorage.setItem('panelRouteName', JSON.stringify(routeName));
    history.push(ROUTES.route);
  };
  const [data, setData] = useState<IDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval>>();

  const load = useCallback(
    async (selected: Period) => {
      try {
        const response = await api.get(
          `/summarizers/management-dashboard?period=${selected}`
        );
        setData(response.data);
      } catch {
        // mantém o último dado na tela; próxima atualização tenta de novo
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    setLoading(true);
    load(period);
    timer.current = setInterval(() => load(period), REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [period, load]);

  const goOrders = () => history.push(ROUTES.order);

  const tabsBar = (
    <Tabs
      value={tab}
      onChange={(_, value) => setTab(value)}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', marginBottom: 16 }}
    >
      <Tab label="Visão Geral" />
      <Tab label="Motoristas" />
      <Tab label="Lead Time" />
      <Tab label="Armazém" />
      <Tab label="Ocorrências" />
    </Tabs>
  );

  // seletor de período das abas novas (mesmo estado da Visão Geral)
  const periodSelector = (
    <Box display="flex" alignItems="center" justifyContent="flex-end" mb={1}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => setPeriodAnchor(event.currentTarget)}
      >
        {PERIOD_LABELS[period]}
      </Button>
      <Menu
        anchorEl={periodAnchor}
        open={Boolean(periodAnchor)}
        onClose={() => setPeriodAnchor(null)}
      >
        {(Object.keys(PERIOD_LABELS) as Period[]).map((option) => (
          <MenuItem
            key={option}
            selected={option === period}
            onClick={() => {
              setPeriod(option);
              setPeriodAnchor(null);
            }}
          >
            {PERIOD_LABELS[option]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  if (tab !== 0) {
    return (
      <Main title="Painel de Gestão">
        {tabsBar}
        {periodSelector}
        {tab === 1 && <DriverScorecardTab period={period} />}
        {tab === 2 && <LeadTimeTab period={period} />}
        {tab === 3 && <WarehouseTab period={period} />}
        {tab === 4 && <OccurrencesTab period={period} />}
      </Main>
    );
  }

  if (loading && !data) {
    return (
      <Main title="Painel de Gestão">
        {tabsBar}
        <Box display="flex" justifyContent="center" p={8}>
          <CircularProgress />
        </Box>
      </Main>
    );
  }

  if (!data) {
    return (
      <Main title="Painel de Gestão">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={12}
          p={8}
        >
          <Typography style={{ color: muted }}>
            Não foi possível carregar o painel. Verifique a conexão com a API
            e tente novamente.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setLoading(true);
              load(period);
            }}
          >
            Tentar novamente
          </Button>
        </Box>
      </Main>
    );
  }

  const { kpis, liveRoutes, actionNeeded, deliveriesByHour, funnel, quality } =
    data;
  const maxHour = Math.max(1, ...deliveriesByHour.map((h) => h.total));
  const maxOccurrence = Math.max(
    1,
    ...actionNeeded.occurrencesToday.map((o) => o.total)
  );
  const noSignalRoutes = liveRoutes.filter((r) => r.situation === 'no-signal');

  return (
    <Main title="Painel de Gestão">
      {tabsBar}
      <Box display="flex" flexDirection="column" gridGap={16}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Operação em tempo real</Typography>
          <Box display="flex" alignItems="center" gridGap={8}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              endIcon={<ExpandMoreIcon />}
              onClick={(event) => setPeriodAnchor(event.currentTarget)}
            >
              {PERIOD_LABELS[period]}
            </Button>
            <Menu
              anchorEl={periodAnchor}
              open={Boolean(periodAnchor)}
              onClose={() => setPeriodAnchor(null)}
            >
              {(Object.keys(PERIOD_LABELS) as Period[]).map((option) => (
                <MenuItem
                  key={option}
                  selected={option === period}
                  onClick={() => {
                    setPeriod(option);
                    setPeriodAnchor(null);
                  }}
                >
                  {PERIOD_LABELS[option]}
                </MenuItem>
              ))}
            </Menu>
            <Typography style={{ fontSize: 12, color: muted }}>
              atualizado {formatHour(data.generatedAt)} · auto 30s
            </Typography>
          </Box>
        </Box>

        {/* KPIs */}
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Pedidos no período"
              value={kpis.totalPeriod}
              info="Pedidos com data no período selecionado. Clique para abrir a lista."
              onClick={() => openOrders('order')}
              detail={
                kpis.totalDelta !== null
                  ? `${kpis.totalDelta >= 0 ? '▲' : '▼'} ${Math.abs(
                      kpis.totalDelta
                    )}% vs anterior`
                  : undefined
              }
              detailColor={kpis.totalDelta && kpis.totalDelta < 0 ? bad : good}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Aguardando separação"
              value={kpis.awaitingSeparation}
              info={`Pedidos abertos dos últimos ${kpis.operationalWindowDays} dias ainda não conferidos na separação. Clique para abrir a lista filtrada.`}
              onClick={() => openOrders('awaiting-separation')}
              detail={`${kpis.awaitingDueToday} com execução p/ hoje · ${kpis.operationalWindowDays}d`}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Paradas em aberto agora"
              value={kpis.inRoute.stops}
              info="Paradas ainda não finalizadas nas rotas de hoje (e em rota dos últimos 2 dias). Clique para abrir as rotas."
              onClick={() => history.push(ROUTES.route)}
              detail={`${kpis.inRoute.routes} em rota · ${kpis.inRoute.notStarted} não iniciada${
                kpis.inRoute.notStarted === 1 ? '' : 's'
              }`}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Entregues"
              value={kpis.deliveredPeriod}
              info="Pedidos entregues no período (data de entrega; sem ela, a última atualização). Clique para abrir a lista."
              onClick={() => openOrders('delivered')}
              detail={
                kpis.deliveredDelta !== null
                  ? `${kpis.deliveredDelta >= 0 ? '▲' : '▼'} ${Math.abs(
                      kpis.deliveredDelta
                    )}% vs anterior`
                  : undefined
              }
              detailColor={
                kpis.deliveredDelta && kpis.deliveredDelta < 0 ? bad : good
              }
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Atrasados"
              value={kpis.late}
              info={`Pedidos abertos dos últimos ${kpis.operationalWindowDays} dias com execução prevista vencida. Clique para abrir a lista.`}
              onClick={() => openOrders('backorders')}
              color={bad}
              detail={`execução vencida · ${kpis.operationalWindowDays}d`}
              detailColor={bad}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Kpi
              label="Parciais em aberto"
              value={kpis.partialsOpen}
              info="Pedidos parcialmente separados com itens ainda aguardando. Clique para abrir a lista filtrada."
              onClick={() => openOrders('partial')}
              color={warn}
              detail={`${kpis.partialPendingItems} itens aguardando`}
              detailColor={warn}
            />
          </Grid>
        </Grid>

        {/* Rotas ao vivo + ação necessária */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Paper style={card} variant="outlined">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                mb={1}
              >
                <Typography style={{ fontSize: 17, fontWeight: 700 }}>
                  Rotas em andamento agora
                </Typography>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <Button
                    size="small"
                    color="primary"
                    variant={mapMode === 'live' ? 'contained' : 'text'}
                    onClick={() =>
                      setMapMode(mapMode === 'live' ? 'off' : 'live')
                    }
                  >
                    Mapa ao vivo
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    variant={mapMode === 'regional' ? 'contained' : 'text'}
                    onClick={() =>
                      setMapMode(mapMode === 'regional' ? 'off' : 'regional')
                    }
                  >
                    Análise regional 30d
                  </Button>
                </Box>
              </Box>

              {mapMode !== 'off' && (
                <Box mb={1} borderRadius={8} overflow="hidden" border="1px solid #E0E0E0">
                  <LiveOperationsMap
                    routes={liveRoutes}
                    showRegional={mapMode === 'regional'}
                  />
                </Box>
              )}

              {liveRoutes.length === 0 && (
                <Typography style={{ color: muted, padding: 16 }}>
                  Nenhuma rota em andamento neste momento.
                </Typography>
              )}

              {liveRoutes.map((route) => (
                <Box
                  key={route.routeId}
                  display="flex"
                  alignItems="center"
                  gridGap={12}
                  py={1}
                  px={0.5}
                  borderBottom="1px solid #F0F0F0"
                  bgcolor={
                    route.situation === 'no-signal'
                      ? '#FFFBF2'
                      : route.situation === 'late'
                      ? '#FDF4F4'
                      : undefined
                  }
                  style={{ opacity: route.situation === 'not-started' ? 0.75 : 1 }}
                >
                  <Box display="flex" alignItems="center" width={160} gridGap={2}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => openRoute(route.routeName)}
                      onKeyDown={(event) =>
                        event.key === 'Enter' && openRoute(route.routeName)
                      }
                      title="Abrir a rota para gestão"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: blue,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {route.routeName}
                    </span>
                    <Tooltip title="Linha do tempo (fotos e assinaturas)" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setTimelineRouteId(route.routeId)}
                      >
                        <HistoryIcon style={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <span style={{ fontSize: 14, flexGrow: 1, minWidth: 120 }}>
                    {route.driverName || '—'}
                  </span>
                  <Box display="flex" alignItems="center" gridGap={8} width={220}>
                    <Box
                      flexGrow={1}
                      height={10}
                      borderRadius={4}
                      bgcolor={track}
                      overflow="hidden"
                      display="flex"
                    >
                      <Box
                        height={10}
                        bgcolor={blue}
                        width={`${
                          (route.delivered / Math.max(1, route.totalStops)) * 100
                        }%`}
                      />
                      {route.undelivered > 0 && (
                        <Box
                          height={10}
                          bgcolor={bad}
                          ml="2px"
                          width={`${
                            (route.undelivered / Math.max(1, route.totalStops)) *
                            100
                          }%`}
                        />
                      )}
                    </Box>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      {route.delivered + route.undelivered}/{route.totalStops}
                    </span>
                  </Box>
                  <span
                    style={{
                      fontSize: 13,
                      width: 90,
                      textAlign: 'center',
                      fontWeight: route.undelivered > 0 ? 700 : 400,
                      color: route.undelivered > 0 ? bad : muted,
                    }}
                  >
                    {route.undelivered} não realiz.
                  </span>
                  <span style={{ fontSize: 13, width: 90 }}>
                    {syncLabel(route)}
                  </span>
                  <Box width={190} display="flex">
                    {situationChip(route)}
                  </Box>
                </Box>
              ))}

              <Box display="flex" gridGap={16} mt={1} alignItems="center">
                <Box display="flex" alignItems="center" gridGap={6}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: blue,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: 12, color: muted }}>entregues</span>
                </Box>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: bad,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: 12, color: muted }}>
                    não realizadas
                  </span>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper style={card} variant="outlined">
              <Typography style={{ fontSize: 17, fontWeight: 700 }}>
                Ação necessária
              </Typography>

              <Box
                border={`1px solid #F0CFCF`}
                bgcolor="#FDF7F7"
                borderRadius={10}
                p={1.5}
                mt={1.5}
              >
                <Box display="flex" justifyContent="space-between">
                  <span style={{ fontSize: 14, fontWeight: 700, color: bad }}>
                    {actionNeeded.late.total} pedidos atrasados
                  </span>
                  <Button size="small" color="primary" onClick={goOrders}>
                    ver lista
                  </Button>
                </Box>
                <Typography style={{ fontSize: 13, color: muted }}>
                  {actionNeeded.late.noSeparation} sem separação ·{' '}
                  {actionNeeded.late.partialStalled} parciais parados ·{' '}
                  {actionNeeded.late.awaitingRoute} aguardando roteirização
                </Typography>
              </Box>

              <Box
                border={`1px solid #F0E3C8`}
                bgcolor="#FFFDF7"
                borderRadius={10}
                p={1.5}
                mt={1.5}
              >
                <Box display="flex" justifyContent="space-between">
                  <span style={{ fontSize: 14, fontWeight: 700, color: warn }}>
                    {kpis.partialsOpen} parciais aguardando estoque
                  </span>
                  <Button size="small" color="primary" onClick={goOrders}>
                    ver lista
                  </Button>
                </Box>
                {actionNeeded.blockingProducts.length > 0 && (
                  <Typography style={{ fontSize: 13, color: muted }}>
                    {actionNeeded.blockingProducts
                      .map(
                        (product) =>
                          `${product.description} trava ${product.ordersBlocked}`
                      )
                      .join(' · ')}
                  </Typography>
                )}
              </Box>

              <Box
                border={`1px solid #E7E9ED`}
                borderRadius={10}
                p={1.5}
                mt={1.5}
              >
                <Typography
                  style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}
                >
                  Ocorrências de hoje (
                  {actionNeeded.occurrencesToday.reduce(
                    (sum, occurrence) => sum + occurrence.total,
                    0
                  )}
                  )
                </Typography>
                <Box display="flex" flexDirection="column" gridGap={7}>
                  {actionNeeded.occurrencesToday.length === 0 && (
                    <Typography style={{ fontSize: 13, color: muted }}>
                      Nenhuma ocorrência hoje.
                    </Typography>
                  )}
                  {actionNeeded.occurrencesToday.map((occurrence) => (
                    <HBar
                      key={occurrence.reason}
                      label={occurrence.reason}
                      value={occurrence.total}
                      max={maxOccurrence}
                    />
                  ))}
                </Box>
              </Box>

              <Box
                border={`1px solid #E7E9ED`}
                borderRadius={10}
                p={1.5}
                mt={1.5}
              >
                <Typography style={{ fontSize: 14, fontWeight: 700 }}>
                  Aparelhos dos motoristas
                </Typography>
                <Typography style={{ fontSize: 13, color: ink }}>
                  <span style={{ color: good }}>●</span>{' '}
                  {liveRoutes.length - noSignalRoutes.length} sincronizando
                  normalmente
                </Typography>
                {noSignalRoutes.length > 0 && (
                  <Typography style={{ fontSize: 13, color: ink }}>
                    <span style={{ color: warn }}>●</span>{' '}
                    {noSignalRoutes.length} sem sinal (
                    {noSignalRoutes
                      .map((route) => route.driverName || route.routeName)
                      .join(', ')}
                    )
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tendências */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper style={card} variant="outlined">
              <Box display="flex" justifyContent="space-between" alignItems="baseline">
                <Box display="flex" alignItems="center" gridGap={6}>
                  <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                    Entregas por hora — hoje
                  </Typography>
                  <Tooltip
                    title="Pedidos entregues hoje, agrupados pela hora local da entrega (sem data de entrega, vale a última atualização)."
                    arrow
                  >
                    <InfoOutlinedIcon style={{ fontSize: 15, color: '#B0BEC5' }} />
                  </Tooltip>
                </Box>
                <Typography style={{ fontSize: 12, color: muted }}>
                  {deliveriesByHour.reduce((sum, h) => sum + h.total, 0)} até
                  agora
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="flex-end"
                gridGap={6}
                height={120}
                mt={2}
              >
                {Array.from({ length: 12 }, (_, index) => index + 7).map(
                  (hour) => {
                    const found = deliveriesByHour.find(
                      (entry) => entry.hour === hour
                    );
                    const value = found?.total || 0;
                    return (
                      <Box
                        key={hour}
                        flex={1}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gridGap={4}
                        title={`${hour}h: ${value} entrega${value === 1 ? '' : 's'}`}
                      >
                        <Box
                          width="100%"
                          borderRadius={4}
                          bgcolor={value > 0 ? blue : track}
                          height={value > 0 ? Math.max(8, (value / maxHour) * 96) : 4}
                        />
                        <span style={{ fontSize: 10, color: '#9E9E9E' }}>
                          {String(hour).padStart(2, '0')}h
                        </span>
                      </Box>
                    );
                  }
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper style={card} variant="outlined">
              <Box display="flex" alignItems="center" gridGap={6}>
                <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                  Funil dos pedidos — período
                </Typography>
                <Tooltip
                  title="Pedidos com data dentro do período selecionado, por etapa: importados → conferidos na separação → roteirizados → entregues."
                  arrow
                >
                  <InfoOutlinedIcon style={{ fontSize: 15, color: '#B0BEC5' }} />
                </Tooltip>
              </Box>
              {funnel.created === 0 && (
                <Typography style={{ fontSize: 13, color: muted, marginTop: 12 }}>
                  Nenhum pedido importado no período selecionado — troque o
                  período no seletor acima.
                </Typography>
              )}
              <Box display="flex" flexDirection="column" gridGap={10} mt={2}>
                <HBar
                  label="Importados"
                  value={funnel.created}
                  max={Math.max(1, funnel.created)}
                  color={blue}
                />
                <HBar
                  label="Separados"
                  value={funnel.conferred}
                  max={Math.max(1, funnel.created)}
                  color={blue}
                />
                <HBar
                  label="Em rota"
                  value={funnel.routed}
                  max={Math.max(1, funnel.created)}
                  color={blue}
                />
                <HBar
                  label="Entregues"
                  value={funnel.delivered}
                  max={Math.max(1, funnel.created)}
                  color={blue}
                />
              </Box>
              <Typography style={{ fontSize: 12, color: muted, marginTop: 12 }}>
                {funnel.created - funnel.conferred} parados entre importação e
                separação.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper style={card} variant="outlined">
              <Box display="flex" alignItems="center" gridGap={6}>
                <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                  Qualidade — 7 dias
                </Typography>
                <Tooltip
                  title="Últimos 7 dias: % de pedidos entregues sem gerar envio parcial; tempo médio entre abrir e concluir a parada; % de paradas não realizadas; % de entregas com assinatura registrada no app."
                  arrow
                >
                  <InfoOutlinedIcon style={{ fontSize: 15, color: '#B0BEC5' }} />
                </Tooltip>
              </Box>
              <Box display="flex" flexDirection="column" gridGap={10} mt={2}>
                <Box>
                  <Typography
                    style={{ fontSize: 26, fontWeight: 700, color: good }}
                  >
                    {quality.firstShipmentRate ?? '—'}%
                  </Typography>
                  <Typography style={{ fontSize: 12, color: muted }}>
                    entregas no 1º envio
                  </Typography>
                </Box>
                <Box>
                  <Typography style={{ fontSize: 26, fontWeight: 700 }}>
                    {quality.avgStopMinutes ?? '—'} min
                  </Typography>
                  <Typography style={{ fontSize: 12, color: muted }}>
                    tempo médio por parada
                  </Typography>
                </Box>
                <Box>
                  <Typography style={{ fontSize: 26, fontWeight: 700 }}>
                    {quality.undeliveredRate ?? '—'}%
                  </Typography>
                  <Typography style={{ fontSize: 12, color: muted }}>
                    não realizadas
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    style={{ fontSize: 26, fontWeight: 700, color: blue }}
                  >
                    {quality.photoSignatureRate ?? '—'}%
                  </Typography>
                  <Typography style={{ fontSize: 12, color: muted }}>
                    entregas com foto e assinatura
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <RouteTimelineDrawer
        routeId={timelineRouteId}
        onClose={() => setTimelineRouteId(null)}
      />
    </Main>
  );
};
